import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Lock, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type TestimonialStatus = "pending" | "approved" | "published" | "rejected" | "archived";

type Row = {
  id: string;
  full_name: string;
  email: string;
  role_title: string | null;
  organization: string | null;
  quote: string;
  context: string | null;
  rating: number | null;
  consent_publish_quote: boolean;
  consent_publish_name: boolean;
  consent_publish_org: boolean;
  consent_use_marketing: boolean;
  release_effective_date: string | null;
  release_expires_at: string | null;
  signature_name: string | null;
  signed_at: string | null;
  status: TestimonialStatus;
  moderator_notes: string | null;
  published_at: string | null;
  created_at: string;
};

/** Statuses the database still allows the submitter to edit. */
const EDITABLE: TestimonialStatus[] = ["pending", "approved"];

const STATUS_COPY: Record<TestimonialStatus, { label: string; detail: string; tone: string }> = {
  pending: {
    label: "Awaiting review",
    detail: "Your testimonial has not been reviewed yet. You can still change anything below.",
    tone: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  },
  approved: {
    label: "Approved — not yet live",
    detail: "Approved for publication. You can still make edits until it goes live.",
    tone: "bg-sky-500/15 text-sky-600 border-sky-500/30",
  },
  published: {
    label: "Published",
    detail: "This testimonial is live, so it is locked. Contact us to request a change or withdrawal.",
    tone: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  },
  rejected: {
    label: "Not accepted",
    detail: "This submission was not accepted for publication and can no longer be edited.",
    tone: "bg-destructive/15 text-destructive border-destructive/30",
  },
  archived: {
    label: "Archived",
    detail: "This testimonial has been archived and can no longer be edited.",
    tone: "bg-muted text-muted-foreground border-border",
  },
};

const today = () => new Date().toISOString().slice(0, 10);

type Draft = {
  role_title: string;
  organization: string;
  quote: string;
  context: string;
  rating: string;
  consent_publish_quote: boolean;
  consent_publish_name: boolean;
  consent_publish_org: boolean;
  consent_use_marketing: boolean;
  release_effective_date: string;
  release_expires_at: string;
  signature_name: string;
};

const toDraft = (row: Row): Draft => ({
  role_title: row.role_title ?? "",
  organization: row.organization ?? "",
  quote: row.quote ?? "",
  context: row.context ?? "",
  rating: row.rating ? String(row.rating) : "",
  consent_publish_quote: row.consent_publish_quote,
  consent_publish_name: row.consent_publish_name,
  consent_publish_org: row.consent_publish_org,
  consent_use_marketing: row.consent_use_marketing,
  release_effective_date: row.release_effective_date ?? today(),
  release_expires_at: row.release_expires_at ?? "",
  signature_name: row.signature_name ?? "",
});

function validate(draft: Draft): string | null {
  if (draft.quote.trim().length < 20) return "Your quote needs to be at least 20 characters.";
  if (draft.quote.trim().length > 1200) return "Please keep your quote under 1200 characters.";
  if (!draft.consent_publish_quote) {
    return "Quote consent is required. To withdraw permission entirely, contact us and we will remove the testimonial.";
  }
  if (draft.signature_name.trim().length < 2) return "Type your full name as your signature.";
  if (!draft.release_effective_date) return "Choose a release effective date.";
  if (draft.release_expires_at && draft.release_expires_at < draft.release_effective_date) {
    return "The release expiry date cannot be before the effective date.";
  }
  if (draft.rating && !/^[1-5]$/.test(draft.rating)) return "Rating must be a number from 1 to 5.";
  return null;
}

const MyTestimonial = () => {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("testimonial_submissions")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Could not load your testimonial", { description: error.message });
      setLoading(false);
      return;
    }
    const list = (data ?? []) as Row[];
    setRows(list);
    setDrafts(Object.fromEntries(list.map((r) => [r.id, toDraft(r)])));
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) void load();
    if (!authLoading && !user) setLoading(false);
  }, [authLoading, user, load]);

  const returnTo = useMemo(
    () => `/auth?next=${encodeURIComponent("/my-testimonial")}`,
    [],
  );

  const save = async (row: Row) => {
    const draft = drafts[row.id];
    if (!draft) return;
    const problem = validate(draft);
    if (problem) {
      toast.error("Please fix this first", { description: problem });
      return;
    }
    setSavingId(row.id);
    const signatureChanged = draft.signature_name.trim() !== (row.signature_name ?? "").trim();
    const { error } = await supabase
      .from("testimonial_submissions")
      .update({
        role_title: draft.role_title.trim() || null,
        organization: draft.organization.trim() || null,
        quote: draft.quote.trim(),
        context: draft.context.trim() || null,
        rating: draft.rating ? Number(draft.rating) : null,
        consent_publish_quote: draft.consent_publish_quote,
        consent_publish_name: draft.consent_publish_name,
        consent_publish_org: draft.consent_publish_org,
        consent_use_marketing: draft.consent_use_marketing,
        release_effective_date: draft.release_effective_date,
        release_expires_at: draft.release_expires_at || null,
        signature_name: draft.signature_name.trim(),
        // Re-signing the release re-stamps the signature timestamp.
        signed_at: signatureChanged ? new Date().toISOString() : row.signed_at,
      })
      .eq("id", row.id)
      .select("id");
    setSavingId(null);
    if (error) {
      toast.error("Update failed", { description: error.message });
      return;
    }
    toast.success("Your testimonial was updated");
    void load();
  };

  if (authLoading || loading) {
    return (
      <main className="container mx-auto flex min-h-[60vh] items-center justify-center px-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!user) {
    return (
      <main className="container mx-auto max-w-xl px-4 py-20">
        <Helmet>
          <title>Edit your testimonial | Dr. Sully Speaks</title>
          <meta name="description" content="Sign in to review or update the testimonial you submitted before it is published." />
        </Helmet>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" /> Sign in to edit your testimonial
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Use the same email address you submitted your testimonial with. We match your release to your verified
              email so nobody else can change your words.
            </p>
            <Button asChild>
              <Link to={returnTo}>Sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="container mx-auto max-w-3xl px-4 py-14">
      <Helmet>
        <title>Edit your testimonial | Dr. Sully Speaks</title>
        <meta name="description" content="Review and update your quote, consents, and release dates before your testimonial is published." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="mb-8 space-y-2">
        <h1 className="font-display text-3xl font-bold">Your testimonial</h1>
        <p className="text-sm text-muted-foreground">
          Signed in as {user.email}. You can update your quote, consents, and release dates until your testimonial is
          published.
        </p>
        <Button variant="ghost" size="sm" onClick={() => void load()} className="px-0">
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </header>

      {rows.length === 0 && (
        <Card>
          <CardContent className="space-y-4 py-8 text-sm text-muted-foreground">
            <p>
              We could not find a testimonial submitted with <strong>{user.email}</strong>. If you used a different
              email address, sign in with that one instead.
            </p>
            <Button asChild variant="outline">
              <Link to="/testimonial-intake">Share a testimonial</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="space-y-8">
        {rows.map((row) => {
          const draft = drafts[row.id];
          if (!draft) return null;
          const editable = EDITABLE.includes(row.status);
          const status = STATUS_COPY[row.status];
          const set = (patch: Partial<Draft>) =>
            setDrafts((d) => ({ ...d, [row.id]: { ...d[row.id], ...patch } }));

          return (
            <Card key={row.id}>
              <CardHeader className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <CardTitle className="text-lg">{row.full_name}</CardTitle>
                  <Badge variant="outline" className={status.tone}>
                    {status.label}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{status.detail}</p>
                {row.moderator_notes && (
                  <p className="rounded-md border border-border bg-muted/40 p-3 text-sm">
                    <span className="font-semibold">Note from our team: </span>
                    {row.moderator_notes}
                  </p>
                )}
              </CardHeader>

              <CardContent className="space-y-6">
                <fieldset disabled={!editable || savingId === row.id} className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor={`role-${row.id}`}>Role / title</Label>
                      <Input
                        id={`role-${row.id}`}
                        value={draft.role_title}
                        onChange={(e) => set({ role_title: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`org-${row.id}`}>Organization</Label>
                      <Input
                        id={`org-${row.id}`}
                        value={draft.organization}
                        onChange={(e) => set({ organization: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`quote-${row.id}`}>Your quote</Label>
                    <Textarea
                      id={`quote-${row.id}`}
                      rows={6}
                      maxLength={1200}
                      value={draft.quote}
                      onChange={(e) => set({ quote: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">{draft.quote.trim().length} / 1200 characters</p>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
                    <div className="space-y-2">
                      <Label htmlFor={`context-${row.id}`}>Context (optional)</Label>
                      <Input
                        id={`context-${row.id}`}
                        value={draft.context}
                        onChange={(e) => set({ context: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`rating-${row.id}`}>Rating (1–5)</Label>
                      <Input
                        id={`rating-${row.id}`}
                        inputMode="numeric"
                        value={draft.rating}
                        onChange={(e) => set({ rating: e.target.value.replace(/[^1-5]/g, "").slice(0, 1) })}
                      />
                    </div>
                  </div>

                  <div className="space-y-3 rounded-lg border border-border p-4">
                    <p className="text-sm font-semibold">Consents</p>
                    {([
                      ["consent_publish_quote", "Publish my quote"],
                      ["consent_publish_name", "Publish my name"],
                      ["consent_publish_org", "Publish my organization"],
                      ["consent_use_marketing", "Use my quote in marketing materials"],
                    ] as const).map(([key, label]) => (
                      <div key={key} className="flex items-start gap-3">
                        <Checkbox
                          id={`${key}-${row.id}`}
                          checked={draft[key]}
                          onCheckedChange={(v) => set({ [key]: v === true } as Partial<Draft>)}
                        />
                        <Label htmlFor={`${key}-${row.id}`} className="text-sm font-normal leading-snug">
                          {label}
                        </Label>
                      </div>
                    ))}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor={`eff-${row.id}`}>Release effective date</Label>
                      <Input
                        id={`eff-${row.id}`}
                        type="date"
                        value={draft.release_effective_date}
                        onChange={(e) => set({ release_effective_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`exp-${row.id}`}>Release expires (optional)</Label>
                      <Input
                        id={`exp-${row.id}`}
                        type="date"
                        value={draft.release_expires_at}
                        onChange={(e) => set({ release_expires_at: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`sig-${row.id}`}>Signature (type your full name)</Label>
                    <Input
                      id={`sig-${row.id}`}
                      value={draft.signature_name}
                      onChange={(e) => set({ signature_name: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Changing your signature re-dates your release to today.
                    </p>
                  </div>
                </fieldset>

                {editable ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <Button onClick={() => void save(row)} disabled={savingId === row.id}>
                      {savingId === row.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                      )}
                      Save changes
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setDrafts((d) => ({ ...d, [row.id]: toDraft(row) }))}
                      disabled={savingId === row.id}
                    >
                      Reset
                    </Button>
                  </div>
                ) : (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ShieldCheck className="h-4 w-4" /> Editing is locked for this testimonial.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </main>
  );
};

export default MyTestimonial;
